import { Table } from '@components/table/Table';
import { TableActions } from '@components/table/tableActions';
import { type CellContext } from '@tanstack/react-table';
import { useAnomalieDetectionList } from 'api/anomalies/anomalyServices';
import { useFeatureFlag } from 'context/FeatureFlagProvider/FeatureFlagProvider';
import useDisclosure from 'hooks/useDisclosure';
import { useServerSideTableSorting } from 'hooks/useServerSideTableSorting';
import { FEATURE_FLAGS_LIST } from 'lib/featureFlagsList';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { UpdateStatusModal } from '../../modal/UpdateStatusModal';
import useGetChangesTableColumns from '../../hooks/usetGetChangesTableColumns/useGetChangesTableColumns';
import { useNavigate } from 'react-router-dom';
import { type Anomaly } from 'interfaces/change/ChangeData.interface';
import { useTranslation } from 'react-i18next';

interface AnomalyTableProps {
  columnVisibility: {
    impact_average_diff_euro: boolean;
    impact_average_diff: boolean;
  };
  query?: string;
  filters?: {
    utilityType?: string;
    statusQuery?: string;
    groupQuery?: number;
    assetQuery?: number;
    showOnlyAssigned?: boolean;
  };
}

const AnomaliesTable: FC<AnomalyTableProps> = ({
  columnVisibility,
  query,
  filters,
}) => {
  const { t } = useTranslation();
  const [anomalyId, setAnomalyId] = useState<number | undefined>();

  const { hasFeatureFlagPermission } = useFeatureFlag();
  const { handleSortChange, orderBy } = useServerSideTableSorting({
    addSortToUrlParams: true,
  });
  const navigate = useNavigate();

  const {
    data: anomaliesList,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
  } = useAnomalieDetectionList({
    query,
    utilityType: filters?.utilityType,
    status: filters?.statusQuery,
    group: filters?.groupQuery,
    asset: filters?.assetQuery,
    showOnlyAssigned: filters?.showOnlyAssigned,
    orderBy,
  });

  const totalDBRowCount = anomaliesList?.pages?.[0]?.total ?? 0;

  const flatData = useMemo(
    () =>
      anomaliesList?.pages?.flatMap(
        (page: any) => page?.anomalies_detected ?? []
      ) ?? [],
    [anomaliesList]
  );

  const {
    isOpen: isUpdateStatusOpen,
    handleClose: handleUpdateStatusClose,
    handleOpen: handleUpdateStatusOpen,
  } = useDisclosure();

  const createTableActions = (info: CellContext<any, any>): ReactNode => {
    const id: number = info?.row?.original?.anomaly_detection_id;
    const actions = hasFeatureFlagPermission(FEATURE_FLAGS_LIST.changes.get)
      ? [
          {
            name: t('common.details'),
            isAnchor: true,
            linkTo: `details/${id}`,
          },
          hasFeatureFlagPermission(FEATURE_FLAGS_LIST.changes.patch_status)
            ? {
                name: t('anomaliesPage.modal.title'),
                onClick: () => {
                  setAnomalyId(id);
                  handleUpdateStatusOpen();
                },
              }
            : null,
        ]
      : [];
    return hasFeatureFlagPermission(FEATURE_FLAGS_LIST.changes.get) ? (
      <TableActions actions={actions} />
    ) : (
      <></>
    );
  };

  const goToDetails = (id?: number): void => {
    if (id) navigate(`details/${id}`);
  };

  const handleRowClick = (row?: Anomaly): void => {
    setAnomalyId(row?.anomaly_detection_id);
    goToDetails(row?.anomaly_detection_id);
  };

  const { columns } = useGetChangesTableColumns({ createTableActions });

  return (
    <>
      <Table
        columns={columns}
        data={flatData}
        enableRowSelection={true}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        totalDBRowCount={totalDBRowCount}
        onClickRow={
          hasFeatureFlagPermission(FEATURE_FLAGS_LIST.changes.get)
            ? handleRowClick
            : undefined
        }
        state={{ columnVisibility }}
        rowEstimateSize={100}
        handleSortChange={handleSortChange}
        manualSorting={true}
      />
      <UpdateStatusModal
        changeId={anomalyId ? anomalyId.toString() : undefined}
        isOpen={isUpdateStatusOpen}
        handleOpen={handleUpdateStatusOpen}
        handleClose={handleUpdateStatusClose}
      />
    </>
  );
};
export default AnomaliesTable;
